import React, { useEffect, useRef } from 'react';

import cn from 'classnames';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';

import { UiButton, UiText } from '@shared/ui';

import cls from './Popup.module.css';

type PopupType = 'alert' | 'confirm' | 'delete';

interface PopupProps {
    isOpen: boolean;
    onHidePopup: () => void;
    zIndex?: number;
    type?: PopupType;
    title?: string;
    contentText?: string;
    confirmText?: string;
    cancelText?: string;
    alertBtnText?: string;
    btnActionCb?: () => void;
    popupContentClassName?: string;
    headerClassName?: string;
    footerClassName?: string;
    isPending?: boolean;
    deleteButtonText?: string;
    cancelButtonText?: string;
    children?: React.ReactNode;
}

const Header = ({ title, headerClassName }: { title: string; headerClassName?: string }) => (
    <header className={cn(cls.popupHeader, headerClassName && headerClassName)}>
        <UiText tag="h2" variant="subHeading" weight="bold" align="center">
            {title}
        </UiText>
    </header>
);

const Body = ({ contentText, children }: { contentText: string; children?: React.ReactNode }) => (
    <UiText align="center" tag="p" variant="subHeading">
        {contentText}
        {children}
    </UiText>
);

export const Popup = ({
    onHidePopup,
    btnActionCb,
    isOpen,
    zIndex,
    title,
    type = 'alert',
    contentText = '',
    alertBtnText,
    popupContentClassName,
    headerClassName,
    footerClassName,
    isPending,
    deleteButtonText,
    cancelButtonText,
    children,
}: PopupProps) => {
    const { t } = useTranslation();
    const nodeRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return () => {};

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || e.defaultPrevented) return;

            e.preventDefault();
            e.stopPropagation();
            onHidePopup();
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                btnActionCb?.();
            }
        };

        window.addEventListener('keydown', handleEscape, true);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleEscape, true);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onHidePopup, btnActionCb]);

    return ReactDOM.createPortal(
        <CSSTransition
            in={isOpen}
            timeout={300}
            classNames={{
                enter: cls.popupEnter,
                enterActive: cls.popupEnterActive,
                exit: cls.popupExit,
                exitActive: cls.popupExitActive,
            }}
            unmountOnExit
            nodeRef={nodeRef}
        >
            <div
                className={cn(cls.customPopup, { [cls.zIndex]: zIndex })}
                style={zIndex ? { zIndex } : undefined}
                ref={nodeRef}
                data-ui-popup="true"
            >
                <div className={cn(cls.customPopupOverlay)} onClick={onHidePopup}>
                    <div
                        className={cn(cls.customPopupContent, popupContentClassName)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {title && <Header title={title} headerClassName={headerClassName} />}
                        {contentText && <Body contentText={contentText}>{children}</Body>}
                        <footer className={cn(cls.popupFooter, footerClassName)}>
                            {type === 'delete' && (
                                <>
                                    <UiButton
                                        btnColor="danger"
                                        btnText={deleteButtonText || t('Popup.delete.deleteBtn')}
                                        onClick={btnActionCb}
                                        disabled={isPending}
                                    />
                                    <UiButton
                                        btnColor="secondary"
                                        btnText={cancelButtonText || t('Popup.delete.cancelBtn')}
                                        onClick={onHidePopup}
                                        disabled={isPending}
                                    />
                                </>
                            )}
                            {type === 'alert' && (
                                <UiButton
                                    btnColor="secondary"
                                    btnText={alertBtnText ?? t('Popup.alert.okBtn')}
                                    onClick={onHidePopup}
                                />
                            )}
                            {type === 'confirm' && (
                                <>
                                    <UiButton
                                        btnColor="primary"
                                        btnText={t('Popup.confirm.confirmBtn')}
                                        onClick={btnActionCb}
                                    />
                                    <UiButton
                                        btnColor="secondary"
                                        btnText={t('Popup.confirm.cancelBtn')}
                                        onClick={onHidePopup}
                                    />
                                </>
                            )}
                        </footer>
                    </div>
                </div>
            </div>
        </CSSTransition>,
        document.body
    );
};
